import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

export const CHART_COLORS = ['#ED8936', '#F6AD55', '#38B2AC', '#4299E1', '#ECC94B', '#F6E05E', '#63B3ED'];

export const chartCardSx = {
  p: 2.5,
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  height: '100%',
};

export const formatCount = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-';
  }
  return value.toLocaleString();
};

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatSeriesLabel = (label: string | number | null | undefined): string => {
  const text = String(label ?? '');
  const match = /^(\d{4})-(\d{2})$/.exec(text);
  if (!match) {
    return text;
  }
  const month = MONTH_ABBREVIATIONS[Number(match[2]) - 1];
  return month ? `${month} ${match[1]}` : text;
};

export const formatProjectLabel = (projectName: string): string => (
  projectName.length > 28 ? `${projectName.slice(0, 28)}...` : projectName
);

type AnalyticsChartTooltipProps = {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    value?: number | string | null;
    color?: string;
    name?: string;
  }>;
};

const analyticsBarTooltipSx = {
  px: 1.5,
  py: 1,
  borderRadius: 1.5,
  bgcolor: 'rgba(20, 28, 43, 0.96)',
  border: '1px solid rgba(255,255,255,0.1)',
  boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
};

export const AnalyticsChartTooltip: React.FC<AnalyticsChartTooltipProps> = ({ active, label, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <Paper elevation={0} sx={analyticsBarTooltipSx}>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.72)' }}>
        Date
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-inverse)' }}>
        {formatSeriesLabel(label) || '-'}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {payload.map((entry, index) => {
          const rawValue = entry?.value;
          const count = typeof rawValue === 'number' ? rawValue : Number(rawValue || 0);
          return (
            <Box key={`${entry?.name || 'value'}-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ color: entry?.color || 'rgba(255,255,255,0.72)' }}>
                {entry?.name || 'Count'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: entry?.color || '#63B3ED' }}>
                {formatCount(Number.isFinite(count) ? count : 0)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};
